/**
 * Settings Page
 *
 * User settings and app configuration.
 */

import { useState } from 'react';
import { useAuthStore } from '../stores';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge } from '../components/ui';
import { clearAllData, getSyncQueueCount } from '../lib/db';

export function SettingsPage() {
  const { user, logout } = useAuthStore();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [syncQueueCount, setSyncQueueCount] = useState<number | null>(null);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    // Will redirect to login via protected route
  };

  const handleRefreshSyncStatus = async () => {
    const count = await getSyncQueueCount();
    setSyncQueueCount(count);
  };

  const handleClearLocalData = async () => {
    if (confirm('Weet je zeker dat je alle lokale data wilt wissen? Dit kan niet ongedaan worden gemaakt.')) {
      await clearAllData();
      alert('Lokale data is gewist. De app wordt opnieuw geladen.');
      window.location.reload();
    }
  };

  return (
    <div className="space-y-lg">
      {/* User info */}
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-md">
            {user?.photos?.thumbnail ? (
              <img
                src={user.photos.thumbnail}
                alt={user.name}
                className="w-16 h-16 rounded-full"
              />
            ) : (
              <div className="w-16 h-16 bg-kleda-primary rounded-full flex items-center justify-center">
                <span className="text-white text-xl font-bold">
                  {user?.name?.charAt(0) || '?'}
                </span>
              </div>
            )}
            <div>
              <p className="font-medium text-kleda-text-main">{user?.name || 'Onbekend'}</p>
              <p className="text-sm text-kleda-text-muted">{user?.email || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Synchronisatie</CardTitle>
            <Badge variant={navigator.onLine ? 'success' : 'warning'}>
              {navigator.onLine ? 'Online' : 'Offline'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-md">
            <div className="flex items-center justify-between">
              <span className="text-kleda-text-muted">Wachtende wijzigingen</span>
              <span className="font-medium text-kleda-text-main">
                {syncQueueCount !== null ? syncQueueCount : '-'}
              </span>
            </div>

            <Button variant="outline" fullWidth onClick={handleRefreshSyncStatus}>
              Status vernieuwen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* App info */}
      <Card>
        <CardHeader>
          <CardTitle>Over de app</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-sm text-sm">
            <div className="flex justify-between">
              <span className="text-kleda-text-muted">Versie</span>
              <span className="text-kleda-text-main">0.1.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-kleda-text-muted">Platform</span>
              <span className="text-kleda-text-main">PWA</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data management */}
      <Card>
        <CardHeader>
          <CardTitle>Gegevensbeheer</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-sm">
            <Button variant="outline" fullWidth onClick={handleClearLocalData}>
              Lokale data wissen
            </Button>
            <p className="text-xs text-kleda-text-muted text-center">
              Wist alle offline opgeslagen gegevens
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Logout */}
      <Card>
        <CardContent>
          <Button
            variant="danger"
            fullWidth
            onClick={handleLogout}
            isLoading={isLoggingOut}
          >
            Uitloggen
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
